/**
 * SEO — reusable component for page-level metadata.
 * Emits <title>, <meta description>, OpenGraph, Twitter cards, canonical URL,
 * and (optionally) JSON-LD structured data.
 *
 * Usage:
 *   <SEO
 *     title="Hospital Supply Backorder Sourcing — Sterile Mate"
 *     description="..."
 *     canonical="/how-it-works/backorder-sourcing"
 *     keywords={["hospital supply backorder", ...]}
 *     schema={backorderFaqSchema}
 *   />
 */
import { useEffect } from "react";
import { Helmet } from "react-helmet-async";

// Static fallback tags in public/index.html are NOT managed by Helmet, so on a
// page that renders <SEO> the head ends up with two descriptions / canonicals and
// crawlers read the first (static) one. Strip the unmanaged duplicates.
const MANAGED_SELECTORS = [
  'meta[name="description"]',
  'meta[name="keywords"]',
  'meta[property="og:title"]',
  'meta[property="og:description"]',
  'meta[property="og:url"]',
  'meta[property="og:image"]',
  'meta[property="og:type"]',
  'meta[property="og:site_name"]',
  'meta[name="twitter:title"]',
  'meta[name="twitter:description"]',
  'meta[name="twitter:image"]',
  'link[rel="canonical"]',
].join(",");

// Snapshot the static index.html tags at import time — before React mounts and
// before Helmet injects anything — so we remove only those, never Helmet's own.
const STATIC_HEAD_TAGS =
  typeof document !== "undefined"
    ? Array.from(document.querySelectorAll(MANAGED_SELECTORS))
    : [];

const SITE_NAME = "Sterile Mate · Powered by AskIsaac";
const DEFAULT_TITLE = "Sterile Mate — Hospital Supply Backorder & Healthcare Facility Management (TX, LA, AL, MS)";
const DEFAULT_DESC =
  "Sterile Mate solves hospital supply backorders across Texas, Louisiana, Alabama, and Mississippi. Sterile processing, EVS, janitorial, water purification, and neuro patty sponges — American Surgical Ray-Cot® and FABCO neuro sponges in stock. One dispatch center. Powered by AskIsaac.";
const DEFAULT_KEYWORDS = [
  "hospital supply backorder",
  "medical supply backorder solution",
  "allocated SKU sourcing",
  "hospital procurement platform",
  "sterile processing supplies",
  // Compliance & regulatory (high-intent B2B)
  "AAMI ST108 water purification systems",
  "AAMI ST108 compliance",
  "AAMI TIR34 sterile processing water standards",
  "TIR34 water quality medical device reprocessing",
  "Joint Commission water compliance",
  "Joint Commission EC.02.05.01",
  "CDC hospital water management program",
  "ASHRAE 188 Legionella compliance",
  "AAMI ST79 steam sterilization",
  "AAMI ST91 flexible endoscope reprocessing",
  "Section 1861 GPO safe harbor",
  // Product-specific & application terms
  "central reverse osmosis system SPD",
  "central RO system sterile processing",
  "hemodialysis water treatment system",
  "medical DI tank exchange",
  "deionization exchange tanks medical",
  "portable RO system hospital",
  "critical water for pharmacy compounding USP 797",
  "endoscope reprocessing water quality",
  "AAMI ST108 category 1 water",
  "AAMI ST108 category 2 water",
  "AAMI ST108 category 3 water",
  // Service & equipment support
  "industrial RO membrane cleaning services",
  "medical water system preventative maintenance",
  "hospital water system validation",
  "SPD water testing services",
  "hospital water treatment service Texas",
  // Existing anchors
  "EVS janitorial hospital",
  "hospital water purification",
  "DI tank exchange hospital",
  "American Surgical neuro sponges",
  "American Surgical Ray-Cot neuro sponges in stock",
  "FABCO neuro patties",
  "FABCO neuro patties in stock",
  "FABCO neurosurgical cottonoids distributor",
  "neuro sponges in stock",
  "neurosurgical cottonoids in stock",
  "neuro patty sponges distributor",
  "neuro patty sponges in stock Texas",
  "healthcare supply chain Texas",
  "Sol-M authorized distributor",
  "BluBox distributor",
  "Aspen Medical distributor",
  "BEAR-ENT distributor USA",
  "hospital marketplace",
  "GPO alternative",
  "non-GPO purchasing",
  "ambulatory surgery center supplies",
  "rural critical access hospital supplier",
  "Texas Medical Center vendor",
  "Houston hospital supplier",
  "medical supply distributor Louisiana",
  "medical supply distributor Alabama",
  "medical supply distributor Mississippi",
  // Diabetes care & management (Vive Precision + Sol-M / Sol-Care lines)
  "diabetes care products",
  "diabetes management supplies",
  "blood glucose monitors for hospitals",
  "blood glucose test strips distributor",
  "glucose meter kit supplier",
  "diabetic discharge kit supplier",
  "easy twist lancets distributor",
  "safety lancets distributor",
  "insulin syringes distributor",
  "insulin pen needles distributor",
  "diabetic foot care supplies",
  "home health diabetes supplies",
];

// Canonical origin — production domain. Preview uses this too so redirects work cleanly.
const CANONICAL_ORIGIN = "https://sterilemate.com";
const OG_IMAGE = `${CANONICAL_ORIGIN}/og-default.jpg`;

export default function SEO({
  title,
  description,
  canonical = "",
  keywords = [],
  image = OG_IMAGE,
  type = "website",
  schema = null,
  noindex = false,
}) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : DEFAULT_TITLE;
  const desc = description || DEFAULT_DESC;
  const kw = Array.from(new Set([...DEFAULT_KEYWORDS, ...keywords])).join(", ");
  const canonicalUrl = canonical.startsWith("http")
    ? canonical
    : `${CANONICAL_ORIGIN}${canonical || ""}`;

  useEffect(() => {
    STATIC_HEAD_TAGS.forEach((el) => el.remove());
  }, [canonicalUrl]);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <meta name="keywords" content={kw} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={image} />

      {/* Geo targeting — Gulf region focus */}
      <meta name="geo.region" content="US-TX" />
      <meta name="geo.placename" content="Houston, TX" />
      <meta name="ICBM" content="29.7604, -95.3698" />

      {/* JSON-LD structured data */}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
    </Helmet>
  );
}

// ---------- Reusable schema builders ----------

export const orgSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Sterile Mate LLC",
  alternateName: ["Sterile Mate", "AskIsaac"],
  url: CANONICAL_ORIGIN,
  logo: {
    "@type": "ImageObject",
    url: `${CANONICAL_ORIGIN}/logo-512.png`,
    width: 512,
    height: 512,
    caption: "Sterile Mate LLC",
  },
  image: `${CANONICAL_ORIGIN}/logo-512.png`,
  description: DEFAULT_DESC,
  foundingDate: "2020",
  telephone: "+1-281-456-3458",
  email: "info@sterilemate.com",
  address: {
    "@type": "PostalAddress",
    streetAddress: "396 W Greens Road, Suite 1139",
    addressLocality: "Houston",
    addressRegion: "TX",
    postalCode: "77067",
    addressCountry: "US",
  },
  areaServed: [
    { "@type": "State", name: "Texas" },
    { "@type": "State", name: "Louisiana" },
    { "@type": "State", name: "Alabama" },
    { "@type": "State", name: "Mississippi" },
  ],
  sameAs: [
    "https://www.linkedin.com/company/sterile-mate",
  ],
  contactPoint: [
    {
      "@type": "ContactPoint",
      telephone: "+1-281-456-3458",
      contactType: "customer service",
      areaServed: ["US-TX", "US-LA", "US-AL", "US-MS"],
      availableLanguage: ["English", "Spanish"],
    },
  ],
};

export const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "MedicalBusiness",
  "@id": `${CANONICAL_ORIGIN}/#org`,
  name: "Sterile Mate LLC",
  url: CANONICAL_ORIGIN,
  telephone: "+1-281-456-3458",
  priceRange: "$$",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Houston",
    addressRegion: "TX",
    postalCode: "77000",
    addressCountry: "US",
  },
  geo: { "@type": "GeoCoordinates", latitude: 29.7604, longitude: -95.3698 },
  areaServed: ["Texas", "Louisiana", "Alabama", "Mississippi"],
  openingHours: "Mo-Su 00:00-23:59",
};

export const backorderFaqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "What is a hospital supply backorder?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "A hospital supply backorder occurs when a distributor cannot fulfill an ordered product because manufacturer allocations, shortages, recalls, or logistics delays have depleted stock. Sterile Mate specializes in resolving these backorders by hunting allocated SKUs across a nationwide supplier network.",
      },
    },
    {
      "@type": "Question",
      name: "How does Sterile Mate solve backorders?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Sterile Mate operates a proprietary intelligence platform (AskIsaac) that tracks supplier inventory in real time across 208+ hospitals and hundreds of authorized distributors. When your primary distributor says 'out of stock,' our team sources the SKU from alternative allocated channels and dispatches it — often the next morning.",
      },
    },
    {
      "@type": "Question",
      name: "Which regions does Sterile Mate serve?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Sterile Mate serves hospitals, health systems, ambulatory surgery centers, and rural critical-access hospitals across Texas, Louisiana, Alabama, and Mississippi, with primary operations in the Texas Medical Center (Houston, TX).",
      },
    },
    {
      "@type": "Question",
      name: "Is Sterile Mate a GPO or non-GPO alternative?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Sterile Mate is a non-GPO procurement partner. We complement or replace traditional GPO agreements with faster response times, transparent pricing, and specialized surgical, sterile-processing, EVS, and water-treatment sourcing.",
      },
    },
    {
      "@type": "Question",
      name: "What products does Sterile Mate distribute?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Sterile Mate is an authorized distributor for Sol-M, BluBox, Aspen Medical, BEAR-ENT (surgical ENT instruments), American Surgical (Ray-Cot® and Delicot® neurosurgical patty sponges), and Filmop USA (janitorial trolleys), plus Green Klean (EPA-registered disinfectants). We also stock FABCO neuro patties as a specialty (Off-The-Grid) item alongside our authorized American Surgical line, and operate a DI tank exchange service for hospital water treatment.",
      },
    },
  ],
};

/**
 * Water services / DI tank service schema — targets B2B industrial + medical
 * water-treatment search queries (AAMI ST108, TIR34, hemodialysis, SPD).
 */
export const waterServiceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Medical Water Treatment & Purification",
  provider: { "@id": `${CANONICAL_ORIGIN}/#org` },
  areaServed: [
    { "@type": "State", name: "Texas" },
    { "@type": "State", name: "Louisiana" },
    { "@type": "State", name: "Alabama" },
    { "@type": "State", name: "Mississippi" },
  ],
  category: [
    "AAMI ST108 water purification systems",
    "AAMI TIR34 sterile processing water",
    "Central reverse osmosis for SPD",
    "Hemodialysis water treatment",
    "Deionization (DI) exchange tanks — medical grade",
    "Industrial RO membrane cleaning",
    "Medical water system preventative maintenance",
    "USP 797 pharmacy compounding water",
  ],
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Medical Water Services",
    itemListElement: [
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "DI Tank Exchange" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "RO Membrane Cleaning" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Water System PM" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "AAMI ST108 Compliance Audit" } },
    ],
  },
};

/**
 * Diabetes care / management schema — targets product-intent searches for
 * glucose monitors, test strips, lancets and diabetic discharge supplies.
 */
export const diabetesCareSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Diabetes Management Supplies",
  serviceType: "Diabetes Care Product Distribution",
  provider: { "@id": `${CANONICAL_ORIGIN}/#org` },
  url: `${CANONICAL_ORIGIN}/solutions/diabetes-management`,
  description:
    "Blood glucose monitoring kits, test strips, lancets, insulin syringes and pen needles for hospital discharge programs, home health agencies and remote patient monitoring — with blood pressure monitors, scales and oximeters alongside. Program pricing, no GPO contract required.",
  areaServed: [
    { "@type": "State", name: "Texas" },
    { "@type": "State", name: "Louisiana" },
    { "@type": "State", name: "Alabama" },
    { "@type": "State", name: "Mississippi" },
  ],
  audience: {
    "@type": "Audience",
    audienceType: "Hospital case management, home health agencies, diabetes educators, remote patient monitoring programs",
  },
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Diabetes Care Products",
    itemListElement: [
      { "@type": "Offer", itemOffered: { "@type": "Product", name: "Blood Glucose Monitoring System Kit", sku: "DMD1079WHT", brand: { "@type": "Brand", name: "Vive Precision" } } },
      { "@type": "Offer", itemOffered: { "@type": "Product", name: "Blood Glucose Test Strips (50 ct / 100 ct)", sku: "DMD1082BLU50CT", brand: { "@type": "Brand", name: "Vive Precision" } } },
      { "@type": "Offer", itemOffered: { "@type": "Product", name: "Easy-Twist Lancets & 5-Level Lancing Device", brand: { "@type": "Brand", name: "Vive Precision" } } },
      { "@type": "Offer", itemOffered: { "@type": "Product", name: "Sol-Care Safety Insulin Syringe", brand: { "@type": "Brand", name: "Sol-Care" } } },
      { "@type": "Offer", itemOffered: { "@type": "Product", name: "Sol-M Standard Pen Needle", brand: { "@type": "Brand", name: "Sol-M" } } },
      { "@type": "Offer", itemOffered: { "@type": "Product", name: "Sol-Care Safety Lancets", brand: { "@type": "Brand", name: "Sol-Care" } } },
    ],
  },
};

export const diabetesFaqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "What diabetes care products does Sterile Mate supply?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Sterile Mate supplies the full diabetes testing chain: Vive Precision blood glucose monitoring kits (meter, five-level lancing device, test strips, lancets, carry case and batteries), replacement test strips in 50 and 100 count, easy-twist lancets, plus the Sol-M and Sol-Care diabetes care line of safety lancets, safety insulin syringes, standard insulin syringes, pen needles and alcohol prep pads.",
      },
    },
    {
      "@type": "Question",
      name: "How long does a glucose kit's test strips last?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "A standard kit ships with 25 test strips and 25 lancets. At twice-daily testing that is roughly twelve days, which is why Sterile Mate stages strip and lancet refills with the kit rather than after it. Strips are stocked in 50 count (2 x 25) and 100 count (2 x 50) packs with standing refill schedules available.",
      },
    },
    {
      "@type": "Question",
      name: "Do you ship diabetes supplies directly to the patient's home?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes. Diabetes kits and refills can be drop-shipped to a patient address with tracking returned to the case manager, or palletized in bulk to a clinic or program site. No GPO contract is required.",
      },
    },
    {
      "@type": "Question",
      name: "How fast is the Vive Precision blood glucose meter?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "The Vive Precision blood glucose monitoring system (DMD1079WHT) returns a result in 5 seconds from a 0.7 microliter sample using no-code, pre-calibrated strips, and stores up to 500 readings with date and time. It pairs with the Vive Precision app, which also holds blood pressure readings.",
      },
    },
    {
      "@type": "Question",
      name: "Is pricing published for diabetes care products?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "No. Diabetes kits, strips and lancets are quoted against your program's enrollment volume so the meter and its consumables are priced together as one program rather than three separate line items. Request program pricing at sterilemate.com/solutions/diabetes-management.",
      },
    },
  ],
};

/**
 * Helper to build TechnicalArticle schema — use on resource / compliance
 * landing pages that biomedical engineers, facility managers, and SPD
 * managers search for by exact code (AAMI ST108, TIR34, etc.).
 */
export function technicalArticleSchema({ headline, description, url, datePublished, dateModified, keywords = [] }) {
  return {
    "@context": "https://schema.org",
    "@type": "TechnicalArticle",
    headline,
    description,
    url: url.startsWith("http") ? url : `${CANONICAL_ORIGIN}${url}`,
    datePublished,
    dateModified: dateModified || datePublished,
    inLanguage: "en-US",
    keywords: keywords.join(", "),
    author: { "@id": `${CANONICAL_ORIGIN}/#org` },
    publisher: { "@id": `${CANONICAL_ORIGIN}/#org` },
    proficiencyLevel: "Expert",
    audience: {
      "@type": "Audience",
      audienceType: "Biomedical engineers, hospital facility managers, sterile processing directors",
    },
  };
}

/**
 * Helper to build Product schema for Marketplace listings.
 * Always emits `availability`. If `price` is set, emits it as a firm Offer;
 * if not, emits an "InStock" Offer with priceSpecification=RequestQuote so
 * Google can still show a Product card with an in-stock badge for RFQ items.
 */
export function productSchema({ name, sku, brand, description, image, url, price, availability = "https://schema.org/InStock" }) {
  const s = {
    "@context": "https://schema.org",
    "@type": "Product",
    name,
    sku,
    description,
    image: image ? (image.startsWith("http") ? image : `${CANONICAL_ORIGIN}${image}`) : undefined,
    url: url && (url.startsWith("http") ? url : `${CANONICAL_ORIGIN}${url}`),
    brand: brand && { "@type": "Brand", name: brand },
  };
  if (price) {
    s.offers = {
      "@type": "Offer",
      priceCurrency: "USD",
      price: String(price),
      availability,
      seller: { "@id": `${CANONICAL_ORIGIN}/#org` },
    };
  } else {
    // RFQ / Request-Pricing item — still declare in-stock availability.
    s.offers = {
      "@type": "Offer",
      availability,
      seller: { "@id": `${CANONICAL_ORIGIN}/#org` },
      priceSpecification: {
        "@type": "PriceSpecification",
        priceCurrency: "USD",
        valueAddedTaxIncluded: false,
        description: "Request pricing — quoted per RFQ",
      },
    };
  }
  return s;
}
